import { useEffect, useState } from "react";
import { Menu, X, Instagram } from "lucide-react";

// Icônes TikTok & Facebook (SVG inline — Lucide ne les propose pas tous)
const TikTokIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M19.321 5.562a5.123 5.123 0 0 1-3.414-1.267 5.133 5.133 0 0 1-1.526-2.74 5.1 5.1 0 0 1-.078-.954h-3.29v13.28c0 1.2-.976 2.176-2.176 2.176a2.176 2.176 0 0 1 0-4.352c.12 0 .237.01.35.028V8.44a5.47 5.47 0 0 0-.35-.011c-3.016 0-5.465 2.449-5.465 5.465 0 3.015 2.449 5.464 5.465 5.464s5.465-2.449 5.465-5.464V8.287a8.397 8.397 0 0 0 4.902 1.566V6.56a5.1 5.1 0 0 1-.883-.058l.083-.94z" />
    </svg>
);
const FacebookIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M13.5 21.5v-7.75h2.6l.4-3H13.5V8.83c0-.87.24-1.46 1.49-1.46H16.6V4.69a21.43 21.43 0 0 0-2.31-.12c-2.29 0-3.86 1.4-3.86 3.96v2.22H7.8v3h2.63v7.75h3.07z" />
    </svg>
);

const links = [
    { href: "#about", label: "À propos" },
    { href: "#portfolio", label: "Portfolio" },
    { href: "#services", label: "Services" },
    { href: "#process", label: "Process" },
    { href: "#contact", label: "Contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/90 backdrop-blur-md border-b border-[#E5E5E5]"
                    : "bg-white/60 backdrop-blur-sm"
            }`}
        >
            <div className="max-w-[1600px] mx-auto px-5 md:px-8 lg:px-16 xl:px-24 h-24 md:h-28 flex items-center justify-between gap-6">
                <a
                    href="#top"
                    data-testid="nav-logo"
                    className="flex items-center gap-3 lg:gap-4 shrink-0"
                >
                    <img
                        src="/logo.png"
                        alt="Logo Mélanie Perez — créatrice UGC à Bordeaux"
                        className="w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 object-contain"
                    />
                    <div className="leading-tight">
                        <p className="font-display text-lg md:text-xl lg:text-2xl tracking-[0.02em] text-ink">
                            Mélanie <span className="text-fuchsia">Perez</span>
                        </p>
                        <p className="hidden md:block text-[9px] lg:text-[10px] uppercase tracking-[0.24em] lg:tracking-[0.28em] text-[#4A4A4A] mt-1">
                            Créatrice de contenu &amp; UGC
                        </p>
                    </div>
                </a>

                <nav className="hidden lg:flex items-center gap-7 xl:gap-10">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s|à/g, "")}`}
                            className="text-[11px] uppercase tracking-[0.22em] text-ink hover:text-fuchsia transition-colors whitespace-nowrap"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-6 shrink-0 pl-6 xl:pl-10">
                    <div className="flex items-center gap-3 pr-5 border-r border-ink/15">
                        <a
                            href="https://www.instagram.com/melanie.creatrice/"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="nav-instagram"
                            className="text-ink hover:text-fuchsia transition-colors"
                            aria-label="Instagram"
                        >
                            <Instagram className="w-4 h-4" />
                        </a>
                        <a
                            href="https://www.tiktok.com/@melanie.creatrice"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="nav-tiktok"
                            className="text-ink hover:text-fuchsia transition-colors"
                            aria-label="TikTok"
                        >
                            <TikTokIcon className="w-4 h-4" />
                        </a>
                        <a
                            href="https://www.facebook.com/melanie.peyre.71?locale=fr_FR"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="nav-facebook"
                            className="text-ink hover:text-fuchsia transition-colors"
                            aria-label="Facebook"
                        >
                            <FacebookIcon className="w-4 h-4" />
                        </a>
                    </div>
                    <a
                        href="#contact"
                        data-testid="nav-cta"
                        className="bg-bordeaux text-white px-4 xl:px-5 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-fuchsia transition-colors whitespace-nowrap"
                    >
                        Travaillons ensemble
                    </a>
                </div>

                <button
                    className="lg:hidden text-ink"
                    onClick={() => setOpen(!open)}
                    data-testid="nav-mobile-toggle"
                    aria-label="Menu"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden bg-white border-t border-[#E5E5E5]">
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {links.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                onClick={() => setOpen(false)}
                                className="text-xs uppercase tracking-[0.22em]"
                                data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s|à/g, "")}`}
                            >
                                {l.label}
                            </a>
                        ))}
                        <a
                            href="#contact"
                            onClick={() => setOpen(false)}
                            className="bg-bordeaux text-white px-5 py-3 text-[11px] uppercase tracking-[0.22em] text-center"
                            data-testid="nav-mobile-cta"
                        >
                            Travaillons ensemble
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
